/**
 * Error Formatter Utility
 *
 * Provides generic error message extraction from various error object structures.
 * Handles error objects from different sources (API, file uploads, network, etc.)
 * and extracts human-readable messages instead of "[object Object]".
 */

import i18next from "i18next";

interface TranslatedMessage {
  key: string;
  defaultValue: string;
}

/**
 * Read a property path from a value of unknown shape, as an optional chain
 * would: undefined as soon as a step is null or undefined.
 */
function readPath(value: unknown, ...keys: string[]): unknown {
  let current = value;

  for (const key of keys) {
    if (current === null || current === undefined) return undefined;
    current = Reflect.get(Object(current), key);
  }

  return current;
}

/** A property path's value when it is a non-empty string, otherwise undefined. */
function readNonEmptyString(value: unknown, ...keys: string[]): string | undefined {
  const found = readPath(value, ...keys);

  return typeof found === "string" && found !== "" ? found : undefined;
}

/**
 * Where a message can live on an error, in the order they are tried. Each
 * reader returns undefined when its field is absent or unusable; note that a
 * string `detail` counts even when it is empty.
 */
const MESSAGE_READERS: Array<(error: unknown) => string | undefined> = [
  // userMessage (from FileUploadError structure)
  (error) => readNonEmptyString(error, "userMessage"),
  // Standard Error.message, unless it is just an object representation
  (error) => {
    const message = readNonEmptyString(error, "message");

    return message !== "[object Object]" ? message : undefined;
  },
  // detail.message (API error responses)
  (error) => readNonEmptyString(error, "detail", "message"),
  // detail string (some APIs return detail as string)
  (error) => {
    const detail = readPath(error, "detail");

    return typeof detail === "string" ? detail : undefined;
  },
  // response.data.message (Axios error structure)
  (error) => readNonEmptyString(error, "response", "data", "message"),
  // response.data.detail.message (nested API error)
  (error) => readNonEmptyString(error, "response", "data", "detail", "message"),
  // response.statusText (HTTP errors), with the status code when there is one
  (error) => {
    const statusText = readNonEmptyString(error, "response", "statusText");

    if (statusText === undefined) return undefined;

    const status = readPath(error, "response", "status");

    return status ? `${statusText} (${String(status)})` : statusText;
  },
];

/**
 * Messages for HTTP status codes. A plain object on purpose: the lookup has
 * always accepted string statuses such as "404".
 */
const STATUS_MESSAGES: Record<string, TranslatedMessage | undefined> = {
  400: { key: "errors.badRequest", defaultValue: "Bad request - please check your input" },
  401: { key: "errors.unauthorized", defaultValue: "Unauthorized - please log in" },
  403: { key: "errors.forbidden", defaultValue: "Forbidden - you don't have permission" },
  404: { key: "errors.notFound", defaultValue: "Resource not found" },
  408: { key: "errors.requestTimeout", defaultValue: "Request timeout" },
  413: { key: "errors.requestTooLarge", defaultValue: "Request too large" },
  415: { key: "errors.unsupportedFileType", defaultValue: "Unsupported file type" },
  429: {
    key: "errors.tooManyRequests",
    defaultValue: "Too many requests - please try again later",
  },
  500: { key: "errors.serverError", defaultValue: "Server error - please try again" },
  502: { key: "errors.badGateway", defaultValue: "Bad gateway - server is unavailable" },
  503: {
    key: "errors.serviceUnavailable",
    defaultValue: "Service unavailable - please try again later",
  },
  504: {
    key: "errors.gatewayTimeout",
    defaultValue: "Gateway timeout - server took too long to respond",
  },
};

const CONNECTION_TIMEOUT: TranslatedMessage = {
  key: "errors.connectionTimeout",
  defaultValue: "Connection timeout - please try again",
};
const UNABLE_TO_CONNECT: TranslatedMessage = {
  key: "errors.unableToConnect",
  defaultValue: "Unable to connect to server",
};

/** Messages for network error codes. A Map, so only these exact codes match. */
const NETWORK_CODE_MESSAGES = new Map<string, TranslatedMessage>([
  ["ECONNABORTED", CONNECTION_TIMEOUT],
  ["ETIMEDOUT", CONNECTION_TIMEOUT],
  ["ENOTFOUND", UNABLE_TO_CONNECT],
  ["ECONNREFUSED", UNABLE_TO_CONNECT],
  [
    "ERR_NETWORK",
    { key: "errors.networkError", defaultValue: "Network error - please check your connection" },
  ],
]);

function translate({ key, defaultValue }: TranslatedMessage): string {
  return i18next.t(key, { defaultValue });
}

/**
 * Map an HTTP status to a message, with a generic one for unlisted codes. The
 * status is keyed as a string, exactly as a property lookup coerces it, so the
 * numeric 404 and the string "404" both match.
 */
function formatStatus(status: unknown): string {
  const statusMessage = STATUS_MESSAGES[String(status)];

  if (statusMessage) {
    return translate(statusMessage);
  }

  return i18next.t("errors.requestFailedWithStatus", {
    defaultValue: `Request failed with status ${String(status)}`,
    status,
  });
}

/** Map an error code to a message, with a generic one for unlisted codes. */
function formatErrorCode(code: string): string {
  const networkMessage = NETWORK_CODE_MESSAGES.get(code);

  if (networkMessage) {
    return translate(networkMessage);
  }

  return i18next.t("errors.errorWithCode", { defaultValue: `Error: ${code}`, code });
}

/**
 * Safely extracts a user-friendly error message from any error object
 *
 * @param error - Any error object (Error, API response, custom error, etc.)
 * @returns Human-readable error message string
 *
 * @example
 * ```typescript
 * try {
 *   await uploadFile(file);
 * } catch (error) {
 *   const message = formatErrorForDisplay(error);
 *   console.error("Operation failed:", message); // Not "[object Object]"
 * }
 * ```
 */
export function formatErrorForDisplay(error: unknown): string {
  // Handle null/undefined
  if (!error) {
    return unexpectedErrorMessage();
  }

  // If error is already a string, return it
  if (typeof error === "string") {
    return error;
  }

  return readErrorMessage(error) ?? describeErrorByKind(error) ?? unexpectedErrorMessage();
}

function unexpectedErrorMessage(): string {
  return i18next.t("errors.unexpected", { defaultValue: "An unexpected error occurred" });
}

/** The first message an error carries, trying each field in MESSAGE_READERS order. */
function readErrorMessage(error: unknown): string | undefined {
  for (const read of MESSAGE_READERS) {
    const message = read(error);

    if (message !== undefined) return message;
  }

  return undefined;
}

/**
 * A message for an error that carries none of its own: from its HTTP status,
 * then its error code, then its name unless that is the generic "Error".
 */
function describeErrorByKind(error: unknown): string | undefined {
  const status = readPath(error, "status") || readPath(error, "response", "status");

  if (status) return formatStatus(status);

  const code = readNonEmptyString(error, "code");

  if (code !== undefined) return formatErrorCode(code);

  const name = readNonEmptyString(error, "name");

  return name !== undefined && name !== "Error" ? name : undefined;
}

/**
 * Formats error for console logging with full context
 *
 * @param error - Any error object
 * @param context - Optional context string (e.g., "File upload", "API call")
 * @returns Formatted log object with human-readable message and full error details
 *
 * @example
 * ```typescript
 * catch (error) {
 *   console.error(formatErrorForLogging(error, "File upload"));
 * }
 * ```
 */
export function formatErrorForLogging(
  error: any,
  context?: string,
): { context?: string; message: string; error: any } {
  return {
    ...(context && { context }),
    message: formatErrorForDisplay(error),
    error, // Include full error object for debugging
  };
}
